import { ReactElement } from 'react'

import { LoadingButton, LoadingButtonProps } from '@mui/lab'
import {
  Box,
  Button,
  IconButton,
  Dialog as MuiDialog,
  Stack,
  SvgIconProps,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  TypographyProps,
} from '@mui/material'

import CloseIcon from '@mui/icons-material/Close'

import { TLGTypography } from '@components/TLGTypography'

import {
  buttonSx,
  buttonsWrapperSx,
  closeIconSx,
  closeIconWrapperSx,
  contentSx,
  dialogIconSx,
  dialogSx,
  firstLineButtonsWrapperSx,
  textSx,
  titleDescriptionSx as titleDescriptionSxStyle,
  titleSectionWrapperSx,
  titleSx as titleSxStyle,
} from './styles'

type DialogButton = {
  text: string
  props?: LoadingButtonProps
  icon?: ReactElement<SvgIconProps>
  onClick?(event: React.MouseEvent): void | Promise<void>
}

type TitleTextProps = {
  label: string
  variant?: TypographyProps['variant']
  sx?: SxProps<Theme>
}

export interface IDialogProps {
  contentDataTestId?: string
  open: boolean
  icon?: ReactElement<SvgIconProps>
  title: string | TitleTextProps
  titleDescription?: string | TitleTextProps
  text?: string | ReactElement
  textBodySx?: SxProps<Theme>
  children?: React.ReactNode
  containerSx?: SxProps<Theme>
  button1: DialogButton
  button2?: DialogButton
  button3?: DialogButton
  button4?: DialogButton
  disableCloseOnBackdropClick?: boolean
  disableCloseOnEscapeKeyDown?: boolean
  buttonsWrapperSx?: SxProps<Theme>
  showCloseButton?: boolean
  onClose?(): void
  dialogPaperPropsSx?: SxProps<Theme>
}

const TLGDialog = ({
  contentDataTestId,
  open,
  icon,
  title,
  titleDescription,
  text,
  textBodySx,
  children,
  containerSx,
  dialogPaperPropsSx,
  button1,
  button2,
  button3,
  button4,
  disableCloseOnBackdropClick,
  disableCloseOnEscapeKeyDown,
  buttonsWrapperSx: buttonsWrapperOverrideSx,
  showCloseButton,
  onClose,
}: IDialogProps) => {
  const hasTitleDescription = !!titleDescription
  const handleOnClose = (_: any, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (disableCloseOnBackdropClick && reason === 'backdropClick') {
      return
    }

    if (disableCloseOnEscapeKeyDown && reason === 'escapeKeyDown') {
      return
    }

    onClose?.()
  }

  const isSomeButtonLoading = [button1, button2, button3, button4].some(
    (v) => v?.props?.loading
  )

  const getDialogButtonElement = ({
    dialogButtonProps,
  }: {
    dialogButtonProps?: DialogButton
  }) => {
    if (dialogButtonProps?.props?.href) {
      return Button
    }

    return LoadingButton
  }

  const Button1Element = getDialogButtonElement({ dialogButtonProps: button1 })
  const Button2Element = getDialogButtonElement({ dialogButtonProps: button2 })
  const Button3Element = getDialogButtonElement({ dialogButtonProps: button3 })
  const Button4Element = getDialogButtonElement({ dialogButtonProps: button4 })

  return (
    <MuiDialog
      data-testid="modal"
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      open={open}
      sx={dialogSx}
      PaperProps={{ sx: dialogPaperPropsSx }}
      onClose={isSomeButtonLoading ? undefined : handleOnClose}
    >
      <Box sx={containerSx} data-testid={contentDataTestId}>
        {showCloseButton && (
          <Box sx={closeIconWrapperSx}>
            <IconButton onClick={onClose}>
              <CloseIcon sx={closeIconSx} />
            </IconButton>
          </Box>
        )}
        <Box sx={{ ...contentSx, ...containerSx } as SxProps<Theme>}>
          {!!icon && (
            <icon.type
              {...icon?.props}
              sx={{ ...dialogIconSx, ...icon?.props.sx }}
            />
          )}

          <Stack sx={titleSectionWrapperSx}>
            <TLGTypography
              data-testid="modal-title"
              id="modal-modal-title"
              variant={(title as TitleTextProps)?.variant ?? 'h5'}
              sx={
                {
                  ...titleSxStyle,
                  ...(title as TitleTextProps)?.sx,
                } as SxProps<Theme>
              }
            >
              {(title as TitleTextProps)?.label ?? title}
            </TLGTypography>

            {hasTitleDescription && (
              <TLGTypography
                data-testid="modal-title-description"
                id="modal-modal-title-description"
                variant={
                  (titleDescription as TitleTextProps)?.variant ?? 'body1'
                }
                sx={
                  {
                    ...titleDescriptionSxStyle,
                    ...(titleDescription as TitleTextProps)?.sx,
                  } as SxProps<Theme>
                }
              >
                {(titleDescription as TitleTextProps)?.label ??
                  titleDescription}
              </TLGTypography>
            )}
          </Stack>

          {!children ? (
            <Typography
              data-testid="modal-description"
              id="modal-modal-description"
              sx={{ ...textSx, ...textBodySx } as SxProps<Theme>}
            >
              {text}
            </Typography>
          ) : (
            children
          )}
        </Box>
        <Stack sx={firstLineButtonsWrapperSx}>
          <Stack
            sx={
              {
                ...buttonsWrapperSx({
                  columnDirection: !!button4,
                }),
                ...buttonsWrapperOverrideSx,
              } as SxProps<Theme>
            }
          >
            <Tooltip title={button1.props?.title}>
              <span>
                <Button1Element
                  data-testid="modal-button-1"
                  variant="outlined"
                  size="medium"
                  disabled={isSomeButtonLoading}
                  {...button1.props}
                  sx={
                    {
                      ...buttonSx,
                      ...button1.props?.sx,
                    } as SxProps<Theme>
                  }
                  onClick={button1.onClick}
                >
                  {button1.text}
                </Button1Element>
              </span>
            </Tooltip>

            {button2 && (
              <Tooltip title={button2.props?.title}>
                <span>
                  <Button2Element
                    data-testid="modal-button-2"
                    variant="outlined"
                    size="medium"
                    disabled={isSomeButtonLoading}
                    {...button2?.props}
                    sx={
                      {
                        ...buttonSx,
                        ...button2.props?.sx,
                      } as SxProps<Theme>
                    }
                    onClick={button2?.onClick}
                  >
                    {button2?.text}
                  </Button2Element>
                </span>
              </Tooltip>
            )}
          </Stack>

          {(button3 || button4) && (
            <Stack
              direction="column"
              sx={
                {
                  ...buttonsWrapperSx({
                    columnDirection: true,
                  }),
                  ...buttonsWrapperOverrideSx,
                } as SxProps<Theme>
              }
            >
              {button3 && (
                <Tooltip title={button3.props?.title}>
                  <span>
                    <Button3Element
                      data-testid="modal-button-3"
                      variant="outlined"
                      size="medium"
                      disabled={isSomeButtonLoading}
                      {...button3?.props}
                      sx={
                        {
                          ...buttonSx,
                          ...button3.props?.sx,
                        } as SxProps<Theme>
                      }
                      onClick={button3?.onClick}
                    >
                      {button3?.text}
                    </Button3Element>
                  </span>
                </Tooltip>
              )}

              {button4 && (
                <Tooltip title={button4.props?.title}>
                  <span>
                    <Button4Element
                      data-testid="modal-button-4"
                      variant="outlined"
                      size="medium"
                      disabled={isSomeButtonLoading}
                      {...button4?.props}
                      sx={
                        {
                          ...buttonSx,
                          ...button4.props?.sx,
                        } as SxProps<Theme>
                      }
                      onClick={button4?.onClick}
                    >
                      {button4?.text}
                    </Button4Element>
                  </span>
                </Tooltip>
              )}
            </Stack>
          )}
        </Stack>
      </Box>
    </MuiDialog>
  )
}

export { TLGDialog }
